import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '@/stores/auth.js';

/**
 * Роуты сервиса Интранет.
 *
 * `/login` и `/auth/callback` — bare-экраны без авторизации и без оболочки.
 * Остальное под `requiresAuth`. Админские разделы дополнительно помечены
 * `requiresAdmin` — гард переадресует на `access-denied`. Права берутся из
 * локального стора (см. stores/auth.ts), с Keycloak-ролями не связаны.
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/pages/HomePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/employees',
      name: 'employees',
      component: () => import('@/pages/EmployeesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Телефонный справочник объединён со страницей сотрудников
      // (переключатель «Карточки/Таблица»). Старый путь — редирект.
      path: '/directory',
      redirect: { name: 'employees', query: { view: 'table' } },
    },
    {
      path: '/org-chart',
      name: 'org-chart',
      component: () => import('@/pages/OrgChartPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/birthdays',
      name: 'birthdays',
      component: () => import('@/pages/BirthdaysPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/vacations',
      name: 'vacations',
      component: () => import('@/pages/VacationsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/documents',
      name: 'documents',
      component: () => import('@/pages/DocumentsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/emergency',
      name: 'emergency',
      component: () => import('@/pages/EmergencyPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/infrastructure',
      name: 'infrastructure',
      component: () => import('@/pages/InfrastructurePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('@/pages/OnboardingPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('@/pages/ProjectsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects/:id',
      name: 'project-detail',
      component: () => import('@/pages/ProjectDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects/:id/areas/:areaId',
      name: 'project-area',
      component: () => import('@/pages/AreaDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assignments',
      name: 'assignments',
      component: () => import('@/pages/AssignmentsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      // «№ 24» в тексте комментария → карточка (до /assignments/:id не дойдёт: два сегмента)
      path: '/assignments/no/:number',
      name: 'assignment-by-number',
      component: () => import('@/pages/AssignmentByNumberPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assignments/:id',
      name: 'assignment-detail',
      component: () => import('@/pages/AssignmentDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin',
      name: 'admin-overview',
      component: () => import('@/pages/admin/AdminOverviewPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/access',
      name: 'admin-access',
      component: () => import('@/pages/admin/AdminAccessPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/employees',
      name: 'admin-employees',
      component: () => import('@/pages/admin/AdminEmployeesPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/report-proxies',
      name: 'admin-report-proxies',
      component: () => import('@/pages/admin/AdminReportProxiesPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/audit',
      name: 'admin-audit',
      component: () => import('@/pages/admin/AdminAuditPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/departments',
      name: 'admin-departments',
      component: () => import('@/pages/admin/AdminDepartmentsPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: () => import('@/pages/AccessDeniedPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/no-access',
      name: 'no-access',
      component: () => import('@/pages/NoAccessPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/pages/AuthCallbackPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
    },
  ],
});

router.beforeEach((to) => {
  const auth = useAuthStore();

  // Гейт входа: залогинен в Keycloak, но интранет недоступен (403 от /me).
  // Держим на экране «нет доступа», не пуская в приложение и не зацикливая
  // на /login (пользователь по факту аутентифицирован в SSO).
  if (auth.accessDenied) {
    return to.name === 'no-access' ? true : { name: 'no-access' };
  }
  if (to.name === 'no-access') {
    return { name: 'home' };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { returnTo: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'home' };
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'access-denied' };
  }
  return true;
});
